import React, { useState } from "react";
import { duplicateArtwork } from "../services/firestore";
import { Artwork } from "../types/artwork";
import "./DuplicateArtworkModal.css";

interface DuplicateArtworkModalProps {
  artwork: Artwork;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newArtworkId: string) => void;
}

const DuplicateArtworkModal: React.FC<DuplicateArtworkModalProps> = ({
  artwork,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [newTitle, setNewTitle] = useState(`${artwork.title} - Copy`);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTitle.trim()) {
      setError("Please enter a title for the duplicate artwork");
      return;
    }

    // Verifica che artwork.id esista
    if (!artwork.id) {
      setError("Invalid artwork ID");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const newArtworkId = await duplicateArtwork(artwork.id, newTitle.trim());
      onSuccess(newArtworkId);
      onClose();
    } catch (error) {
      console.error("Error duplicating artwork:", error);
      setError("Failed to duplicate artwork. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div onClick={handleBackdropClick} className="duplicate-modal-backdrop">
      {/* Modal */}
      <div className="duplicate-modal-container">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="duplicate-modal-close"
        >
          ✕
        </button>

        {/* Header */}
        <div className="duplicate-modal-header">
          <div className="duplicate-modal-header-icon">📋</div>
          <h2 className="duplicate-modal-title">Duplicate Artwork</h2>
          <p className="duplicate-modal-subtitle">
            Create a complete copy of "
            <span className="duplicate-modal-subtitle-artwork">
              {artwork.title}
            </span>
            " including all operation history
          </p>
        </div>

        {/* Preview Card */}
        <div className="duplicate-modal-preview">
          <h4 className="duplicate-modal-preview-title">
            <span className="duplicate-modal-preview-title-icon">📊</span>
            What will be copied:
          </h4>

          <div className="duplicate-modal-preview-grid">
            {[
              { icon: "👨‍🎨", label: "Artist", value: artwork.artist },
              {
                icon: "🏛️",
                label: "Institution",
                value: artwork.institution,
              },
              { icon: "📍", label: "Location", value: artwork.location },
              { icon: "📞", label: "Contact", value: artwork.contact },
            ].map((item, index) => (
              <div key={index} className="duplicate-modal-preview-item">
                <span className="duplicate-modal-preview-item-icon">
                  {item.icon}
                </span>
                <span className="duplicate-modal-preview-item-label">
                  {item.label}:
                </span>
                <span className="duplicate-modal-preview-item-value">
                  {item.value}
                </span>
              </div>
            ))}
          </div>

          {/* Operation History Info */}
          <div className="duplicate-modal-info-box operations">
            <span className="duplicate-modal-info-icon">📝</span>
            <span className="duplicate-modal-info-label">
              Operation History:
            </span>
            <span className="duplicate-modal-info-text">
              All operations and notes will be copied
            </span>
          </div>

          {/* GPS Coordinates Info */}
          {artwork.coordinates && (
            <div className="duplicate-modal-info-box coordinates">
              <span className="duplicate-modal-info-icon">🌍</span>
              <span className="duplicate-modal-info-label">
                GPS Coordinates:
              </span>
              <span className="duplicate-modal-info-text">
                Lat: {artwork.coordinates.lat.toFixed(4)}, Lng:{" "}
                {artwork.coordinates.lng.toFixed(4)}
              </span>
            </div>
          )}

          {/* Status Reset Note */}
          <div className="duplicate-modal-info-box note">
            <span className="duplicate-modal-info-icon">ℹ️</span>
            <span className="duplicate-modal-info-label">Note:</span>
            <span className="duplicate-modal-info-text">
              Status will be reset to "Pending" for the new artwork
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="duplicate-modal-form">
          <div className="duplicate-modal-form-group">
            <label className="duplicate-modal-label">
              <span className="duplicate-modal-label-icon">🎨</span>
              New Artwork Title:{" "}
              <span className="duplicate-modal-label-required">*</span>
            </label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                setError(null);
              }}
              required
              className="duplicate-modal-input"
              placeholder="Enter the title for the duplicate artwork..."
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="duplicate-modal-error">
              <div className="duplicate-modal-error-content">
                <span className="duplicate-modal-error-icon">⚠️</span>
                <span className="duplicate-modal-error-text">{error}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="duplicate-modal-actions">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="duplicate-modal-button duplicate-modal-button-cancel"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading || !newTitle.trim()}
              className="duplicate-modal-button duplicate-modal-button-submit"
            >
              {loading ? (
                <>
                  <div className="duplicate-modal-spinner"></div>
                  Creating Complete Copy...
                </>
              ) : (
                <>
                  <span className="duplicate-modal-button-icon">📋</span>
                  Create Complete Duplicate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuplicateArtworkModal;
